'use client';

import {
  ProductDetail,
  Tag,
  openUrl,
  useBreakpoints,
} from '@nettbureau/featured-core';
import {
  Button,
  Chip,
  Color,
  HStack,
  Text,
  VStack,
} from '@nettbureau/vibrant-ui';
import { useTranslate } from '@tolgee/react';
import { useContext } from 'react';
import { ConfigContext } from '../provider/config-provider';
import { ProductCardDetail } from './ProductCardDetail';
import { ProductCardTitle } from './ProductCardTitle';

interface ProductDetailsProps {
  href?: string;
  title: string;
  tags?: Tag[] | null;
  bodyLine1: string | null;
  bodyLine2: ProductDetail[] | null;
}

export function ProductDetails({
  href,
  title,
  tags,
  bodyLine1,
  bodyLine2,
}: ProductDetailsProps) {
  const { isMobile } = useBreakpoints();
  const { t } = useTranslate();
  const { moreInfoButtonOnProductCards } = useContext(ConfigContext);
  const maxDescriptionLength = 100;

  return (
    <VStack justify="space-between" gap="md" height="100%">
      {tags && (
        <HStack gap="md" wrap padding={{ bottom: 'md' }}>
          {tags.map((tag, index) => (
            <Chip key={index} size="xs" color={tag.color as Color}>
              {tag.name}
            </Chip>
          ))}
        </HStack>
      )}

      <ProductCardTitle href={href} title={title} />

      {bodyLine1 && (
        <Text size="sm" block data-featured-card-body align="left">
          {bodyLine1.length > maxDescriptionLength
            ? bodyLine1.slice(0, maxDescriptionLength).trimEnd() + '...'
            : bodyLine1}
        </Text>
      )}

      {bodyLine2 &&
        (isMobile ? (
          <Text
            color="gray"
            colorTone={400}
            size="sm"
            block
            data-featured-card-body
            align="left"
          >
            {bodyLine2.map((detail) => detail.value).join(', ')}
          </Text>
        ) : (
          <VStack gap="sm">
            {bodyLine2.slice(0, 3).map((detail) => {
              return <ProductCardDetail key={detail.key} detail={detail} />;
            })}
          </VStack>
        ))}
      {href && moreInfoButtonOnProductCards && (
        <HStack>
          <Button
            width="100%"
            color="gray"
            variant="bold"
            size="lg"
            onPress={() => {
              openUrl(href, false);
            }}
          >
            {t('more_information')}
          </Button>
        </HStack>
      )}
    </VStack>
  );
}
